import React from 'react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Euro, 
  Building2,
  ArrowLeft,
  Clock,
  Calendar,
  AlertTriangle,
  Info,
  Sparkles
} from 'lucide-react';

export default function SeuilsMarchesArticle() {
  return (
    <>
      <SEO 
        title="Seuils Marchés Publics 2026 | Guide Complet"
        description="Guide complet sur les seuils des marchés publics en 2026 : MAPA, procédures formalisées, marchés européens, mise à jour au 1er janvier 2026."
        path="/blog/seuils-marches-publics-2026"
      />
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="font-bold text-xl">Mon DCE Express</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b">
        <div className="container mx-auto px-4 py-3">
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700">
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>
        </div>
      </div>

      {/* Article */}
      <article>
        <header className="bg-gradient-to-b from-green-50 to-white py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                Réglementation
              </span>
              <span className="flex items-center gap-1 text-slate-500 text-sm">
                <Calendar className="w-4 h-4" />
                25 janvier 2026
              </span>
              <span className="flex items-center gap-1 text-slate-500 text-sm">
                <Clock className="w-4 h-4" />
                6 min de lecture
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Seuils des Marchés Publics 2026 : Le Guide Actualisé
            </h1>
            
            <p className="text-xl text-slate-600">
              Tous les seuils en vigueur pour 2026 : MAPA, appels d'offres, procédures formalisées. 
              Comprendre ces seuils est essentiel pour savoir quelle procédure s'applique à votre marché.
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="container mx-auto px-4 max-w-4xl py-12">
          
          {/* Alert */}
          <Card className="bg-amber-50 border-amber-200 mb-8">
            <CardContent className="p-6 flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-amber-900 mb-2">Mise à jour importante</h3>
                <p className="text-amber-800">
                  Les seuils européens sont révisés tous les deux ans. Les montants ci-dessous sont 
                  valables du 1er janvier 2024 au 31 décembre 2025, puis seront ajustés pour 2026-2027.
                  Vérifiez toujours les dernières publications officielles.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Introduction */}
          <section className="mb-12">
            <p className="text-lg text-slate-700 mb-6">
              Les <strong>seuils des marchés publics</strong> déterminent la procédure de passation applicable. 
              En dessous de certains montants, la procédure est simplifiée (MAPA). Au-dessus, des procédures 
              formalisées s'imposent, avec publication obligatoire au BOAMP et/ou au JOUE.
            </p>
          </section>

          {/* Seuils État */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Euro className="w-7 h-7 text-green-600" />
              Seuils pour l'État et ses établissements publics
            </h2>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse border border-slate-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left p-4 font-semibold border-b">Type de marché</th>
                    <th className="text-right p-4 font-semibold border-b">Seuil MAPA</th>
                    <th className="text-right p-4 font-semibold border-b">Seuil européen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-4 font-medium">Fournitures et services</td>
                    <td className="p-4 text-right">&lt; 143 000 € HT</td>
                    <td className="p-4 text-right text-green-700 font-semibold">≥ 143 000 € HT</td>
                  </tr>
                  <tr className="border-b bg-slate-50">
                    <td className="p-4 font-medium">Travaux</td>
                    <td className="p-4 text-right">&lt; 5 538 000 € HT</td>
                    <td className="p-4 text-right text-green-700 font-semibold">≥ 5 538 000 € HT</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-medium">Services sociaux et spécifiques</td>
                    <td className="p-4 text-right">&lt; 750 000 € HT</td>
                    <td className="p-4 text-right text-green-700 font-semibold">≥ 750 000 € HT</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Seuils Collectivités */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Building2 className="w-7 h-7 text-blue-600" />
              Seuils pour les collectivités territoriales
            </h2>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse border border-slate-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left p-4 font-semibold border-b">Type de marché</th>
                    <th className="text-right p-4 font-semibold border-b">Seuil MAPA</th>
                    <th className="text-right p-4 font-semibold border-b">Seuil européen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-4 font-medium">Fournitures et services</td>
                    <td className="p-4 text-right">&lt; 221 000 € HT</td>
                    <td className="p-4 text-right text-blue-700 font-semibold">≥ 221 000 € HT</td>
                  </tr>
                  <tr className="border-b bg-slate-50">
                    <td className="p-4 font-medium">Travaux</td>
                    <td className="p-4 text-right">&lt; 5 538 000 € HT</td>
                    <td className="p-4 text-right text-blue-700 font-semibold">≥ 5 538 000 € HT</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-medium">Services sociaux et spécifiques</td>
                    <td className="p-4 text-right">&lt; 750 000 € HT</td>
                    <td className="p-4 text-right text-blue-700 font-semibold">≥ 750 000 € HT</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Petits marchés */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Les petits marchés (gré à gré)</h2>
            
            <Card className="bg-green-50 border-green-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-green-900 mb-3">Marchés sans publicité ni mise en concurrence</h3>
                <p className="text-green-800 mb-4">
                  Pour les marchés de <strong>moins de 40 000 € HT</strong>, l'acheteur peut contracter 
                  directement avec l'entreprise de son choix, sans formalité particulière.
                </p>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-slate-600">
                    <Info className="w-4 h-4 inline mr-1 text-green-600" />
                    C'est une excellente opportunité pour les PME ! Prospectez directement les acheteurs publics 
                    pour ces petits marchés qui ne sont pas publiés.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Procédures */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Quelle procédure selon le montant ?</h2>
            
            <div className="space-y-4">
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">0 - 40 000 € HT : Gré à gré</h3>
                  <p className="text-slate-600">
                    Pas de publicité obligatoire. L'acheteur peut contracter directement.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">40 000 € - Seuil européen : MAPA</h3>
                  <p className="text-slate-600">
                    Marché à Procédure Adaptée. Publicité adaptée, procédure simplifiée. 
                    Publication sur profil d'acheteur ou presse locale.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-violet-500">
                <CardContent className="p-6">
                  <h3 className="font-bold text-slate-900 mb-2">≥ Seuil européen : Procédure formalisée</h3>
                  <p className="text-slate-600">
                    Appel d'offres ouvert ou restreint, procédure concurrentielle avec négociation, 
                    dialogue compétitif. Publication au BOAMP et JOUE obligatoire.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Key points */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Points clés à retenir</h2>
            
            <ul className="space-y-4">
              {[
                'Les seuils s\'appliquent sur le montant estimé HT du marché',
                'Pour les marchés reconduits, c\'est le montant total sur la durée qui compte',
                'Un allotissement ne doit pas servir à contourner les seuils',
                'Les seuils européens sont révisés tous les 2 ans (prochaine révision : 2026)',
                'En dessous de 40 000 €, les opportunités ne sont pas publiées : prospectez directement !'
              ].map((point, i) => (
                <li key={`item-${i}`} className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-violet-100 text-violet-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-slate-700">{point}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Conclusion */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Conclusion</h2>
            <p className="text-slate-600 mb-4">
              Connaître les seuils des marchés publics vous permet de comprendre les procédures applicables 
              et d'adapter votre stratégie de réponse. Les MAPA offrent souvent plus de flexibilité et 
              représentent une excellente porte d'entrée pour les PME.
            </p>
            <p className="text-slate-600">
              Avec <strong>Mon DCE Express</strong>, accédez aux marchés de toutes tailles, du petit MAPA 
              au grand appel d'offres européen, avec une analyse IA pour identifier ceux qui correspondent 
              le mieux à votre profil.
            </p>
          </section>

        </div>

        {/* CTA */}
        <section className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Trouvez les marchés à votre taille
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              MAPA, appels d'offres formalisés, marchés européens... Notre IA vous recommande 
              les opportunités adaptées à votre entreprise.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100">
                <Sparkles className="w-5 h-5 mr-2" />
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </section>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="container mx-auto px-4 text-center text-sm">
          <p>© 2026 Mon DCE Express - Tous droits réservés</p>
        </div>
      </footer>
    </div>
    </>
  );
}
